/**
 * Cliente del endpoint /api/v1/projects.
 * Backend: app/api/v1/projects.py.
 *
 * En B.5.c.2 solo necesitamos `listProjects` para el dashboard del workspace.
 * Acciones de mutación (createProject, updateProject) llegan en B.5.e con el
 * wizard de creación.
 */

import { apiFetch } from './client'
import type { ArtifactSourceMode } from './artifacts'

export type ProjectMethodology =
  | 'waterfall'
  | 'scrum'
  | 'kanban'
  | 'hybrid'

export type ProjectStatus =
  | 'draft'
  | 'active'
  | 'on_hold'
  | 'closed'
  | 'cancelled'

export type ContentLanguage = 'es' | 'en'

export interface Project {
  id: string
  tenant_id: string
  code: string
  name: string
  client_name: string | null
  methodology: ProjectMethodology
  status: ProjectStatus
  content_language: ContentLanguage
  template_snapshot_id: string | null
  start_date: string | null
  end_date: string | null
  metadata: Record<string, unknown>
  /** Doc 21 Fase 5 — id del lifecycle template que el proyecto usa.
   *  Necesario para resolver el subphase correcto del workflow cuando
   *  el snapshot tiene múltiples lifecycles (system default + clones). */
  lifecycle_template_id: string | null
  methodology_template_id: string | null
  created_at: string
  updated_at: string
  created_by_user_id: string | null
  /** Doc 26 §2.3 — singleton del Project Lead. FK al `project_member` que
   * lidera el proyecto. NULL si el proyecto aún no tiene líder asignado
   * (sin team todavía, o el lead fue removido). El frontend lo usa para
   * el badge "Líder activo" y para habilitar "Transferir liderazgo". */
  lead_member_id: string | null
  /** Doc 26 §2.3 — `user_id` resuelto del lead_member (JOIN en backend,
   * no es columna). NULL cuando `lead_member_id` es NULL o el member
   * apunta a un stakeholder externo sin user vinculado. */
  lead_user_id: string | null
}

export interface ListProjectsFilters {
  project_status?: ProjectStatus | undefined
}

export async function listProjects(
  accessToken: string,
  filters?: ListProjectsFilters,
): Promise<Project[]> {
  const params = new URLSearchParams()
  if (filters?.project_status)
    params.set('project_status', filters.project_status)
  const qs = params.toString()
  return apiFetch<Project[]>(
    `/api/v1/projects${qs ? `?${qs}` : ''}`,
    { accessToken },
  )
}

export async function getProject(
  accessToken: string,
  projectId: string,
): Promise<Project> {
  return apiFetch<Project>(`/api/v1/projects/${projectId}`, { accessToken })
}

// ─── Mutations ─────────────────────────────────────────────────────────────
/**
 * Body para crear un proyecto.
 *
 * `code` es obligatorio en backend pero el wizard genera uno por defecto
 * a partir del client_name + sequence si el usuario no lo escribe (ver
 * `buildAutoProjectCode` en el wizard).
 *
 * `metadata` lleva la intención del wizard que el backend aún no
 * materializa nativamente (lifecycle_template_key, cadence, phases
 * contract_mode). Cuando el `project_service` evolucione (B.5.e.3) podrá
 * leer estos campos del metadata para crear el lifecycle real.
 */
export interface CreateProjectBody {
  code: string
  name: string
  methodology: ProjectMethodology
  content_language: ContentLanguage
  client_name?: string | undefined
  start_date?: string | undefined
  end_date?: string | undefined
  metadata?: Record<string, unknown> | undefined
  /** AI-1.f.bug: lifecycle template seleccionado en el wizard. Cuando
   * se manda, el backend materializa el snapshot completo + crea las
   * project_phases + los expected_artifacts (artifacts vacíos en
   * estado draft) declarados en el lifecycle. Sin este campo, el
   * proyecto se crea sin lifecycle real (no recomendado en flujo normal). */
  lifecycle_template_id?: string | undefined
  /** Reservado — methodology templates aún no implementados. */
  methodology_template_id?: string | undefined
  /** OT-0065 — rol (`template_key`) con el que el usuario actual entra al
   * equipo. El backend lo da de alta como líder en la misma transacción. */
  creator_role: string
}

export async function createProject(
  accessToken: string,
  body: CreateProjectBody,
): Promise<Project> {
  return apiFetch<Project>('/api/v1/projects', {
    accessToken,
    method: 'POST',
    body,
  })
}

/**
 * Hard-delete del proyecto. Borra todas las relaciones vía CASCADE en PG
 * (phases, members, artifacts, versions, approvals, snapshot). El backend
 * también dispara los cleanups externos cuando estén integrados (Blob,
 * Qdrant, Neo4j) — ver `ProjectService.delete_project` TODOs.
 *
 * Acción **irreversible**. El caller debe presentar confirmación
 * destructiva (type-to-confirm con el nombre del proyecto) antes de
 * llamar.
 */
export async function deleteProject(
  accessToken: string,
  projectId: string,
): Promise<void> {
  return apiFetch<void>(`/api/v1/projects/${projectId}`, {
    accessToken,
    method: 'DELETE',
  })
}

// ─── Lifecycle (B.5.d.4) ────────────────────────────────────────────────────

// Note: 'external_canon' is the literal enum value from the API contract;
// kept verbatim. UI renders it as "Aportado por el cliente" / "Client-provided".
export type ContractMode = 'internal' | 'external_canon' | 'skipped'

export type ProjectPhaseStatus =
  | 'not_started'
  | 'in_progress'
  | 'blocked'
  | 'approved'
  | 'skipped'

export interface ProjectSubphase {
  subphase_code: string
  subphase_order: number
  /** Nombre humano resuelto del lifecycle correcto + i18n del
   * Accept-Language. Cae al `subphase_code` si no hay match. */
  name: string
  description: string | null
  role_template_key: string | null
  /** Display name del rol responsable resuelto del catálogo de roles
   * congelado en el snapshot. `null` cuando la sub-fase no declara rol. */
  role_name: string | null
}

export interface ProjectPhase {
  id: string
  project_id: string
  phase_template_id: string | null
  phase_code: string
  phase_order: number
  contract_mode: ContractMode
  owner_user_id: string | null
  gate_description: string | null
  status: ProjectPhaseStatus
  started_at: string | null
  completed_at: string | null
  /** Counts por status. Keys: 'draft', 'in_review', 'approved',
   * 'superseded', 'canon' (= total is_canon=true). API contract — keys
   * here mirror backend response shape. */
  artifact_counts: Record<string, number>
  /** Refactor Plan B (Plan-B labels) — labels resueltos del lifecycle
   * correcto del snapshot por el backend. El frontend solo renderiza,
   * cero lógica de selección o resolución i18n. */
  name: string
  description: string | null
  subphases: ProjectSubphase[]
}

export interface ProjectLifecycle {
  project: Project
  phases: ProjectPhase[]
}

export async function fetchProjectLifecycle(
  accessToken: string,
  projectId: string,
): Promise<ProjectLifecycle> {
  return apiFetch<ProjectLifecycle>(
    `/api/v1/projects/${projectId}/lifecycle`,
    { accessToken },
  )
}

/** OT-0058 — rol asignable en el equipo del proyecto. */
export interface ProjectRole {
  template_key: string
  /** Resuelto al `Accept-Language`. */
  name: string
  description: string | null
  /** `null` = el rol no declara alcance y cuenta como interno. */
  applicable_scopes: string[] | null
}

/** Roles que se pueden asignar en Equipo & RACI: los de la metodología del
 * ciclo, más los que piden los flujos de aprobación del proyecto (aunque
 * no sean responsables de ningún documento), más los que ya tienen sus
 * miembros. El backend valida las altas contra esta misma lista. */
export async function fetchProjectRoles(
  accessToken: string,
  projectId: string,
): Promise<ProjectRole[]> {
  return apiFetch<ProjectRole[]>(`/api/v1/projects/${projectId}/roles`, {
    accessToken,
  })
}

// ─── Official Browser (B.5.d.4) ─────────────────────────────────────────────
// Backend endpoint path is `/canon` (API contract). Frontend exposes it as
// "official" everywhere user-facing. `is_canon` field is also raw from API.

export interface OfficialArtifact {
  id: string
  code: string | null
  name: string
  template_key: string
  template_version: string
  subphase_code: string | null
  status: string
  is_canon: boolean
  /** OT-0027 — los documentos aportados como archivo (`upload`) no
   *  ofrecen "Descargar PDF": el backend lo rechaza con 409 y para esos
   *  existe la descarga del archivo original. */
  source_mode: ArtifactSourceMode
  current_version_id: string | null
  current_version_number: number | null
  current_version_hash: string | null
  current_version_approved_at: string | null
  created_at: string
  updated_at: string
}

export interface OfficialByPhase {
  phase_code: string
  artifacts: OfficialArtifact[]
}

export interface ProjectOfficial {
  project_id: string
  total_artifacts: number
  groups: OfficialByPhase[]
}

export interface FetchOfficialOptions {
  include_superseded?: boolean | undefined
}

/** Lista artefactos oficiales del proyecto agrupados por phase_code. Para el
 * Official Browser (B.6). Backend path is `/canon` (API contract). */
export async function fetchProjectOfficial(
  accessToken: string,
  projectId: string,
  options?: FetchOfficialOptions,
): Promise<ProjectOfficial> {
  const params = new URLSearchParams()
  if (options?.include_superseded) params.set('include_superseded', 'true')
  const qs = params.toString()
  return apiFetch<ProjectOfficial>(
    `/api/v1/projects/${projectId}/canon${qs ? `?${qs}` : ''}`,
    { accessToken },
  )
}

// ─── Snapshot (B.6.a — para Official Viewer) ───────────────────────────────

/** Item del snapshot del proyecto: una plantilla congelada con su
 * definition completa (i18n + sections estructurales). El Canon Viewer
 * busca aquí el template usado por un artifact (`artifact.template_key`)
 * para saber cómo renderizar cada section del content. */
export interface SnapshotTemplateItem {
  template_id: string
  template_key: string
  template_type: string
  is_system_default: boolean
  version: string
  version_id: string
  /** Schema completo de la plantilla. Doc 06 §1.1. */
  definition: Record<string, unknown>
}

/** Resumen ligero de un template del snapshot (sin secciones ni prompts IA).
 *  Lo consume el picker de creación de artefactos. */
export interface SnapshotTemplateSummary {
  template_key: string
  template_type: string
  version: string | null
  phase_id: string | null
  /** Sub-fase declarada por el lifecycle (OT-0011) — no `phase_id`, que
   *  viene de la propia plantilla. Es la autoridad sobre dónde cae el
   *  documento; el picker la envía como `subphase_code` al crear. */
  subphase_id: string | null
  i18n: Record<string, { name?: string; description?: string }> | null
}

/** Un template del snapshot con su `definition` completa. Un fetch por vista
 *  (editor / viewer / preview) — el snapshot es inmutable, así que la query
 *  debe cachearse con `staleTime: Infinity`. */
export async function fetchSnapshotTemplate(
  accessToken: string,
  projectId: string,
  templateKey: string,
): Promise<SnapshotTemplateItem> {
  return apiFetch<SnapshotTemplateItem>(
    `/api/v1/projects/${projectId}/snapshot/templates/${encodeURIComponent(templateKey)}`,
    { accessToken },
  )
}

export interface SnapshotTemplateSummaryFilters {
  /** Normalmente 'artifact' para el picker de creación. */
  template_type?: string | undefined
  /** OT-0011 — código de fase (ej. `phase-3`). Limita la respuesta a las
   *  plantillas que el lifecycle declara repetibles (`user_can_add_more`)
   *  para esa fase. Sin este filtro, comportamiento sin cambios. */
  addable_in_phase?: string | undefined
}

/** Resúmenes de los templates del snapshot, opcionalmente filtrados por tipo
 *  y/o por fase agregable (picker de creación de artefactos). */
export async function fetchSnapshotTemplateSummaries(
  accessToken: string,
  projectId: string,
  filters?: SnapshotTemplateSummaryFilters,
): Promise<SnapshotTemplateSummary[]> {
  const params = new URLSearchParams()
  if (filters?.template_type) params.set('template_type', filters.template_type)
  if (filters?.addable_in_phase)
    params.set('addable_in_phase', filters.addable_in_phase)
  const qs = params.toString()
  return apiFetch<SnapshotTemplateSummary[]>(
    `/api/v1/projects/${projectId}/snapshot/templates${qs ? `?${qs}` : ''}`,
    { accessToken },
  )
}

/** OT-0011 — queryKey único para "plantillas agregables en esta fase",
 *  compartido por `PhaseDrillIn` (gatea el botón) y `CreateArtifactDialog`
 *  (alimenta el picker), para que ambos usen el mismo cache de TanStack
 *  Query sin duplicar el array a mano. */
export function addableTemplatesQueryKey(projectId: string, phaseCode: string) {
  return [
    'projects',
    projectId,
    'snapshot',
    'template-summaries',
    'artifact',
    'addable_in_phase',
    phaseCode,
  ] as const
}

// ─── Submit-for-review preview (Doc 21 Fase 5++) ──────────────────────────
/** Refactor Plan B — preview del workflow ya resuelto por el backend
 * (lifecycle correcto + i18n + roles + members). El frontend solo
 * renderiza estos datos; ya no duplica la lógica de resolución que
 * antes vivía en `SubmitForReviewDialog`. */
export interface RequiredRolePreview {
  role_key: string
  role_label: string
}

export interface ApproverPreview {
  user_id: string | null
  display_name: string
  role_key: string
  role_label: string
  is_self: boolean
}

/** Códigos del backend para la razón por la que un stage no tiene
 *  approvers resolubles. None = stage OK. Cada code mapea a una
 *  copy específica en i18n (submit_dialog.stage.warning_*). */
export type StageWarningCode =
  | 'NO_APPROVERS_AT_ALL'
  | 'ONLY_AUTHOR_HAS_ROLE'
  | 'NO_ACTIVE_LEADER'
  | 'APPROVERS_PENDING_INVITATION'

export interface StagePreview {
  order: number
  name: string
  required_roles: RequiredRolePreview[]
  approvers: ApproverPreview[]
  min_approvals_label: string
  exclude_self: boolean
  warning_code: StageWarningCode | null
  will_be_skipped: boolean
}

export interface SubmitPreview {
  source: 'empty' | 'lifecycle_template' | 'lifecycle_override'
  template_name: string | null
  template_key: string | null
  stages: StagePreview[]
  allow_user_override: boolean
  /** El flujo no se puede resolver y el envío fallaría con este mismo
   *  código (copy en `errors:`). Sin stages. */
  error_code: 'WORKFLOW_TEMPLATE_NOT_FOUND' | null
}

// ─── Project Lead transfer + Phase Owner override (Doc 26 §2.3-2.4) ────────

/** Body para `POST /projects/{id}/transfer-ownership`. El backend valida
 * que el `new_lead_member_id` exista en el proyecto y que el role del
 * member tenga `is_project_lead=true` en el catálogo (system o clone
 * del tenant). `reason` queda en `audit_events.payload`. */
export interface TransferOwnershipBody {
  new_lead_member_id: string
  reason?: string | null | undefined
}

/** Transfiere formalmente el liderazgo del proyecto al member indicado.
 * Devuelve el proyecto con el nuevo `lead_member_id`. Idempotente: si el
 * member ya es el lead, no-op silencioso. */
export async function transferOwnership(
  accessToken: string,
  projectId: string,
  body: TransferOwnershipBody,
): Promise<Project> {
  return apiFetch<Project>(
    `/api/v1/projects/${projectId}/transfer-ownership`,
    { accessToken, method: 'POST', body },
  )
}

/** PATCH del owner_user_id de una phase específica. `null` limpia el
 * override (la phase vuelve a resolver al lead default). Devuelve la
 * phase con `name`, `subphases` y labels ya resueltos al locale del
 * Accept-Language (mismo shape que `fetchProjectLifecycle.phases[]`). */
export async function updatePhaseOwner(
  accessToken: string,
  projectId: string,
  phaseId: string,
  ownerUserId: string | null,
): Promise<ProjectPhase> {
  return apiFetch<ProjectPhase>(
    `/api/v1/projects/${projectId}/phases/${phaseId}`,
    {
      accessToken,
      method: 'PATCH',
      body: { owner_user_id: ownerUserId },
    },
  )
}

/** Pide al backend el preview del workflow de aprobación que se
 * ejecutaría al submitir el artifact. El backend resuelve:
 *  · Lifecycle correcto del snapshot (prioridad por lifecycle_template_id).
 *  · Workflow (override embebido vs referencia a template).
 *  · Stages, roles, approvers cruzando con project_members.
 *  · Labels al locale del Accept-Language. */
export async function fetchSubmitPreview(
  accessToken: string,
  projectId: string,
  params: {
    subphase_code: string | null
    author_user_id: string | null
  },
): Promise<SubmitPreview> {
  const search = new URLSearchParams()
  if (params.subphase_code) search.set('subphase_code', params.subphase_code)
  if (params.author_user_id)
    search.set('author_user_id', params.author_user_id)
  const qs = search.toString()
  return apiFetch<SubmitPreview>(
    `/api/v1/projects/${projectId}/submit-preview${qs ? `?${qs}` : ''}`,
    { accessToken },
  )
}
